import { Button, Card, Group, Stack, Text, Title, useMantineColorScheme, Box } from "@mantine/core";
import { Link } from "react-router-dom";

/**
 * News item displayed in the user's news feed.
 */
export interface NewsItem {
  id: string;
  title: string;
  postedAt: string; // ISO
  excerpt?: string;
}

// Default to empty array - news data source does not exist yet
const defaultNews: NewsItem[] = [];

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

/**
 * UserNewFeed component
 *
 * Renders a simple list of news items with a themed card wrapper.
 * Provides an optional "More Details" button for navigation.
 *
 * TODO: Map `items` from API when available.
 */
export interface UserNewsFeedProps {
  title?: string;
  items?: NewsItem[];
  /** Route to navigate for the expanded news view */
  moreDetailsTo?: string;
  /** When true, the Card fills the parent height and the list area scrolls internally */
  fullHeight?: boolean;
  /** Custom message to display when there are no news items */
  emptyMessage?: string;
}

export default function UserNewFeed({ title = "News Feed", items = defaultNews, moreDetailsTo, fullHeight, emptyMessage }: UserNewsFeedProps) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  return (
    <Card withBorder radius="md" p="md" style={{ height: fullHeight ? '100%' : undefined, width: '100%', display: 'flex' }}>
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        <Group justify="space-between" align="center">
          {/* Title color is scheme-aware for stronger contrast. To change globally, see src/theme.ts or swap theme in appThemes. */}
          <Title order={4} c={isDark ? 'gray.0' : 'dark.7'}>{title}</Title>
        </Group>

        <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {items.length === 0 ? (
            <Text c={isDark ? 'gray.5' : 'gray.6'} size="sm">{emptyMessage ?? "No news yet."}</Text>
          ) : (
            <Stack gap={10}>
              {items.map((n) => (
                <Group key={n.id} align="flex-start">
                  <Text fw={600} size="sm" style={{ flex: 1 }}>
                    {n.title}
                  </Text>
                  {/* Avoid overly-dim text in dark mode for better readability */}
                  <Text size="sm" c={isDark ? undefined : 'dimmed'}>
                    {formatDate(n.postedAt)}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Box>

        {moreDetailsTo && (
          <Group justify="flex-end">
            <Button component={Link} to={moreDetailsTo} variant={isDark ? 'filled' : 'light'} size="xs">
              More Details
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
